import { z } from 'zod';
import { exerciseContentSchema } from './exercises.js';
/**
 * Zod-spiegel van specs/exercise-analysis.schema.json (v1.0). Elke
 * provider-output gaat hier doorheen, ook die van de mock. Onbekend = null.
 */
export const analysisCategorySchema = z.enum([
    'WARMUP',
    'SERVE',
    'PASS_RECEPTION',
    'SET',
    'ATTACK',
    'BLOCK',
    'DEFENSE',
    'GAMEPLAY',
    'CONDITIONING',
]);
/** AI-categorie → id in de categorietabel (migratie s2_exercises). */
export const ANALYSIS_CATEGORY_ID = {
    WARMUP: 'cat_warmup',
    SERVE: 'cat_serve',
    PASS_RECEPTION: 'cat_pass_reception',
    SET: 'cat_set',
    ATTACK: 'cat_attack',
    BLOCK: 'cat_block',
    DEFENSE: 'cat_defense',
    GAMEPLAY: 'cat_gameplay',
    CONDITIONING: 'cat_conditioning',
};
export const exerciseAnalysisSchema = z
    .object({
    schemaVersion: z.literal('1.0'),
    language: z.literal('nl'),
    title: z.string().max(160).nullable(),
    summary: z.string().max(1200).nullable(),
    primaryCategory: analysisCategorySchema.nullable(),
    objective: z.string().nullable(),
    instructions: z.array(z.string()).max(30),
    playerSetup: z.string().nullable(),
    minPlayers: z.number().int().min(1).nullable(),
    maxPlayers: z.number().int().min(1).nullable(),
    equipment: z.array(z.string()),
    coachingPoints: z.array(z.string()),
    variations: z.array(z.string()),
    durationMinutes: z.number().int().min(1).nullable(),
    observations: z.array(z.strictObject({ timestampSeconds: z.number().min(0).nullable(), description: z.string() })),
    uncertainties: z.array(z.string()),
    requiresReview: z.literal(true),
})
    .strict();
// --- Media ------------------------------------------------------------------------
export const VIDEO_MIME_TYPES = ['video/mp4', 'video/quicktime'];
export const videoMimeTypeSchema = z.enum(VIDEO_MIME_TYPES, {
    message: 'Kies een MP4- of MOV-video.',
});
/** Pilotgrenzen (docs/04); de server leest de werkelijke waarden uit de config. */
export const MAX_VIDEO_BYTES = 500 * 1024 * 1024;
export const MAX_VIDEO_SECONDS = 10 * 60;
/** "10 minuten" (of seconden als het geen hele minuten zijn). */
export const durationLimitText = (seconds) => seconds % 60 === 0
    ? `${seconds / 60} ${seconds === 60 ? 'minuut' : 'minuten'}`
    : `${seconds} ${seconds === 1 ? 'seconde' : 'seconden'}`;
/** Voor meldingen: "tot 500 MB en maximaal 10 minuten". */
export const videoLimitsText = (maxSeconds = MAX_VIDEO_SECONDS, maxBytes = MAX_VIDEO_BYTES) => `tot ${Math.round(maxBytes / 1024 ** 2)} MB en maximaal ${durationLimitText(maxSeconds)}`;
export const createMediaRequestSchema = z.object({
    mimeType: videoMimeTypeSchema,
    bytes: z
        .number()
        .int()
        .min(1)
        .max(MAX_VIDEO_BYTES, {
        message: `Video is te groot. Kies een video ${videoLimitsText()}.`,
    }),
    rightsDeclaration: z.literal(true, {
        message: 'Bevestig dat je de rechten op deze opname hebt.',
    }),
});
export const mediaStatusSchema = z.enum(['UPLOADING', 'READY', 'ANALYZED', 'FAILED', 'DELETED']);
export const analysisStatusSchema = z.enum([
    'QUEUED',
    'PREPARING',
    'PROCESSING',
    'SUCCEEDED',
    'FAILED',
    'CANCELLED',
]);
export const analysisJobSchema = z.object({
    id: z.string(),
    mediaId: z.string(),
    status: analysisStatusSchema,
    reviewStatus: z.enum(['PENDING', 'ACCEPTED', 'DISCARDED']),
    /** Alleen bij SUCCEEDED. */
    result: exerciseAnalysisSchema.nullable(),
    /** Nederlandse melding bij FAILED; nooit de technische fout van de provider. */
    errorMessage: z.string().nullable(),
    exerciseId: z.string().nullable(),
    createdAt: z.string(),
    finishedAt: z.string().nullable(),
});
export const mediaSchema = z.object({
    id: z.string(),
    status: mediaStatusSchema,
    mimeType: z.string(),
    bytes: z.number().int(),
    durationSeconds: z.number().nullable(),
    createdAt: z.string(),
    latestJob: analysisJobSchema.nullable(),
    /** Hoeveel analyses er voor deze video nog mogen (1 + heranalyses). */
    analysesLeft: z.number().int(),
});
export const mediaResponseSchema = z.object({ media: mediaSchema });
export const analyzeMediaRequestSchema = z.object({
    privacyConfirmation: z.literal(true, {
        message: 'Bevestig dat je toestemming hebt van iedereen die herkenbaar in beeld is.',
    }),
});
export const analysisResponseSchema = z.object({ analysis: analysisJobSchema });
/** Opslaan na review: altijd als privéconcept (CLAUDE.md regel 10). */
export const acceptAnalysisRequestSchema = z.object({
    content: exerciseContentSchema,
    reviewConfirmed: z.literal(true, { message: 'Bevestig dat je alle velden hebt gecontroleerd.' }),
});
//# sourceMappingURL=analysis.js.map